import React from "react";
import { typography, surfaces, borders, textColors, interactive, brandColors } from "../../design-system/tokens";
import { formatFreeTrialLabel } from "../utils/couponUtils";
import { computeStackedPrice, computeTrialDays } from "../utils/discountStacking";

const CouponIcon = ({ className = "", title = "Coupon" }) => (
  <svg role="img" aria-label={title} viewBox="0 0 24 24" fill="none" stroke="currentColor" className={className}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M7 6h10a2 2 0 0 1 2 2v1a2 2 0 0 0 0 4v1a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-1a2 2 0 0 0 0-4V8a2 2 0 0 1 2-2Z"
    />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v8" />
  </svg>
);

const SparkleIcon = ({ className = "", title = "Global discount" }) => (
  <svg role="img" aria-label={title} viewBox="0 0 24 24" fill="none" stroke="currentColor" className={className}>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 2l1.2 4.2L17.5 8 13.2 9.2 12 13.5 10.8 9.2 6.5 8l4.3-1.8L12 2Z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 14l.8 2.7L8.5 18l-2.7.8L5 21l-.8-2.2L1.5 18l2.7-1.3L5 14Z" />
  </svg>
);

const StackIcon = ({ className = "", title = "Stacked discounts" }) => (
  <svg role="img" aria-label={title} viewBox="0 0 24 24" fill="none" stroke="currentColor" className={className}>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3l9 5-9 5-9-5 9-5Z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l9 5 9-5" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 17l9 5 9-5" />
  </svg>
);

const CheckIcon = () => (
  <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const XIcon = () => (
  <svg className="w-5 h-5 text-gray-300 dark:text-dark-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const formatDateShort = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

const PLAN_CARD_STYLES = {
  popular: "border-blue-600 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20",
  // Current plan should not be visually "loud"—we show only the badge.
  current: `${borders.default} ${surfaces.primary}`,
  default: `${borders.default} ${surfaces.primary}`,
};

const BUTTON_STYLES = {
  current: `${surfaces.tertiary} ${textColors.muted} cursor-not-allowed opacity-60`,
  popular: `${interactive.primaryBg} ${interactive.primaryHover} ${interactive.primaryText}`,
  // All upgrade buttons should be the same blue "upgrade" button.
  default: `${interactive.primaryBg} ${interactive.primaryHover} ${interactive.primaryText}`,
};

const getPlanCardStyle = ({ isPopular, isCurrent }) => {
  if (isCurrent) return PLAN_CARD_STYLES.current;
  if (isPopular) return PLAN_CARD_STYLES.popular;
  return PLAN_CARD_STYLES.default;
};

const getButtonStyle = ({ isPopular, isCurrent }) => {
  if (isCurrent) return BUTTON_STYLES.current;
  if (isPopular) return BUTTON_STYLES.popular;
  return BUTTON_STYLES.default;
};

const toNumber = (value) => {
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : 0;
};

const formatMoney = (value) =>
  toNumber(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatPrice = (value) => `$${formatMoney(value)}`;

const formatTrialLabel = (daysRaw) => formatFreeTrialLabel(daysRaw);

const formatDiscountAddon = ({ base, discounted }) => {
  const b = toNumber(base);
  const d = toNumber(discounted);
  if (!Number.isFinite(b) || b <= 0) return null;
  if (!Number.isFinite(d) || d >= b) return null;

  const amountOff = Math.max(0, b - d);
  const pctRaw = (amountOff / b) * 100;
  const pctRounded = Math.round(pctRaw);
  // Prefer percent when it's effectively an integer percent (common in coupons).
  if (Math.abs(pctRaw - pctRounded) < 0.05) return `${pctRounded}% off`;
  return `$${amountOff.toFixed(2)} off`;
};

const formatTrialWithDiscountLabel = ({ trialDays, base, discounted }) => {
  const trial = formatTrialLabel(trialDays);
  if (!trial) return null;
  const addon = formatDiscountAddon({ base, discounted });
  return addon ? `${trial} + ${addon}` : trial;
};

const getSingleDiscountAddonLabel = (discounts = []) => {
  const list = Array.isArray(discounts) ? discounts.filter(Boolean) : [];
  const percent = list.find((d) => d?.percent_off != null)?.percent_off;
  if (percent != null) return `${Number(percent)}% off`;
  const amount = list.find((d) => d?.amount_off != null)?.amount_off;
  if (amount != null) return `$${Number(amount).toFixed(2)} off`;
  return null;
};

const calcAnnualSavingsPercent = (monthlyPrice, yearlyPrice) => {
  const monthly = toNumber(monthlyPrice);
  const yearly = toNumber(yearlyPrice);
  if (monthly <= 0 || yearly <= 0) return 0;
  const baseline = monthly * 12;
  if (baseline <= 0) return 0;
  const pct = (1 - yearly / baseline) * 100;
  return Math.max(0, Math.round(pct));
};

const getMinAnnualSavingsPercent = (plans) => {
  const percents = (plans || []).map((p) => calcAnnualSavingsPercent(p.monthly_price, p.yearly_price)).filter((p) => p > 0);
  if (percents.length === 0) return 0;
  return Math.min(...percents);
};

const AvailablePlans = ({ plans, currentPlan, showAnnualPricing, setShowAnnualPricing, onSelectPlan, couponUI, banner, accessGrant }) => {
  // Hide Free from the upgrade grid: users on Free can upgrade, but we don't show a Free card.
  const displayPlans = (plans || []).filter((p) => p?.name !== "free");
  const minAnnualSavings = getMinAnnualSavingsPercent(displayPlans);
  const planCount = displayPlans.length;
  const lgColsClass = planCount <= 2 ? "lg:grid-cols-2" : planCount === 3 ? "lg:grid-cols-3" : "lg:grid-cols-4";
  const planBadgeTextClass = "px-3 py-1 rounded-full text-xs font-medium tracking-wide text-white";
  const couponPillClass =
    "inline-flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1 rounded-full border bg-green-100 dark:bg-green-900/30 border-green-200 dark:border-green-800 text-green-800 dark:text-green-300 max-w-full";
  const couponBoxClass =
    "w-full max-w-[calc(100%)] sm:max-w-sm mx-auto border bg-green-100 dark:bg-green-900/30 border-green-200 dark:border-green-800 rounded-lg px-2 sm:px-3 py-2";
  const couponGreenText = "text-green-700 dark:text-green-300";
  const couponApplied = Boolean(couponUI?.couponCode);
  const shouldShowBanner = Boolean(banner);
  const hasAccessGrantPlanInGrid = Boolean(accessGrant?.is_active && displayPlans.some((p) => p?.name === accessGrant?.plan_name));

  return (
    <div className={`rounded-lg shadow-sm ${surfaces.primary} border ${borders.default} overflow-hidden max-w-full`}>
      <div className={`px-4 sm:px-6 py-4 border-b ${borders.default} overflow-hidden`}>
        <div className="flex flex-col items-center text-center gap-3 max-w-full">
          <div className="flex items-center justify-center gap-2 sm:gap-3 flex-wrap">
            <h2 className={`${typography.h5} ${textColors.primary}`}>Available Plans</h2>
            {couponUI?.toggleLink && !couponApplied && (
              <button
                type="button"
                onClick={couponUI.toggleLink}
                className={`inline-flex items-center gap-2 ${typography.bodySmall} ${interactive.link} ${interactive.linkHover}`}
              >
                <CouponIcon className={`w-4 h-4 ${interactive.link}`} />
                I have a coupon
              </button>
            )}
            {couponApplied && typeof couponUI?.clear === "function" && (
              <button
                type="button"
                onClick={couponUI.clear}
                className={`inline-flex items-center gap-2 ${typography.bodySmall} ${interactive.link} ${interactive.linkHover}`}
              >
                <CouponIcon className={`w-4 h-4 ${interactive.link}`} />
                Clear coupon
              </button>
            )}
          </div>

          <div className="flex items-center justify-center gap-4">
            <span
              className={`${typography.bodySmall} ${!showAnnualPricing ? "font-medium" : "font-normal"} ${
                !showAnnualPricing ? textColors.primary : textColors.secondary
              }`}
            >
              Monthly
            </span>
            <button
              onClick={() => setShowAnnualPricing(!showAnnualPricing)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                showAnnualPricing ? `${interactive.primaryBg}` : "bg-gray-200 dark:bg-dark-700"
              }`}
              aria-label="Toggle annual pricing"
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  showAnnualPricing ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
            <span
              className={`${typography.bodySmall} ${showAnnualPricing ? "font-medium" : "font-normal"} ${
                showAnnualPricing ? textColors.primary : textColors.secondary
              }`}
            >
              Annual
              {minAnnualSavings > 0 && (
                <span className={`ml-1 ${typography.bodySmall} font-medium text-green-600 dark:text-green-400`}>
                  (Save +{minAnnualSavings}%)
                </span>
              )}
            </span>
          </div>

          {/* Coupon explanation */}
          {couponApplied && couponUI?.message && (
            <div className="flex items-center justify-center gap-2 flex-wrap max-w-full">
              <span className={`${couponPillClass} ${typography.caption} font-medium`}>
                <CouponIcon className="w-4 h-4 flex-shrink-0" title="Coupon applied" />
                <span className="truncate">{couponUI.message}</span>
              </span>
            </div>
          )}

          {accessGrant?.is_active ? (
            <div className={`${typography.caption} ${textColors.tertiary}`}>
              Access coupon active · ends {formatDateShort(accessGrant.expires_at) || "soon"}
            </div>
          ) : null}

          {couponApplied && couponUI?.renewalApplies === false && (
            <div className={`${typography.caption} ${textColors.tertiary}`}>Coupon applies to the first invoice only.</div>
          )}
        </div>

        {couponUI?.isOpen && (
          <div className="mt-4 flex flex-col items-center justify-center gap-2 text-center">
            {/* Coupon entry: only show when not yet applied (invalid coupons keep Apply visible). */}
            {!couponApplied && (
            <div className="flex flex-col sm:flex-row items-center justify-center gap-2 w-full">
              <input
                value={couponUI.value}
                onChange={(e) => couponUI.setValue?.(e.target.value)}
                placeholder="Enter coupon code"
                className={`w-full sm:w-64 px-3 py-2 rounded-lg border ${borders.default} ${surfaces.primary} ${textColors.primary}`}
              />
              <div className="flex items-center justify-center gap-2">
                <button
                  type="button"
                  onClick={couponUI.apply}
                  disabled={couponUI.loading}
                  className={`px-4 py-2 rounded-lg ${typography.button} ${brandColors.primaryBg} ${brandColors.primaryBgHover} ${interactive.primaryText} transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {couponUI.loading ? "Checking…" : "Apply"}
                </button>
                </div>
              </div>
            )}
            {couponUI?.error && (
              <div className="w-full max-w-sm mx-auto rounded-lg border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-900/20 px-3 py-2">
                <p className={`${typography.bodySmall} text-red-700 dark:text-red-300`}>{couponUI.error}</p>
              </div>
            )}
          </div>
        )}

        {shouldShowBanner && <div className="mt-4">{banner}</div>}
      </div>

      <div className="p-4 sm:p-6">
          <div
          className={`grid grid-cols-1 md:grid-cols-2 ${lgColsClass} gap-4 sm:gap-6 items-stretch w-full ${
            hasAccessGrantPlanInGrid ? "mt-8 sm:mt-6" : ""
          }`}
          >
          {displayPlans.map((plan) => {
              const isCurrent = currentPlan?.name === plan.name;
              const isPopular = !isCurrent && plan?.name === "pro";
            const currentOrder = Number(currentPlan?.order ?? 0);
            const targetOrder = Number(plan?.order ?? 0);
            const isDowngrade = !!currentPlan && targetOrder < currentOrder;
              const isAccessGrantPlan = Boolean(accessGrant?.is_active && accessGrant?.plan_name === plan.name);

              const annualSavings = calcAnnualSavingsPercent(plan.monthly_price, plan.yearly_price);
              const discountMonthly = couponUI?.getDisplayPrice ? couponUI.getDisplayPrice(plan, "monthly") : null;
              const discountYearly = couponUI?.getDisplayPrice ? couponUI.getDisplayPrice(plan, "yearly") : null;

              const baseMonthly = toNumber(plan.monthly_price);
              const baseYearly = toNumber(plan.yearly_price);
              const monthlyDiscounted =
                Array.isArray(discountMonthly?.discounts) && discountMonthly.discounts.length
                  ? toNumber(computeStackedPrice({ base: baseMonthly, discounts: discountMonthly.discounts }))
                  : null;
              const yearlyDiscounted =
                Array.isArray(discountYearly?.discounts) && discountYearly.discounts.length
                  ? toNumber(computeStackedPrice({ base: baseYearly, discounts: discountYearly.discounts }))
                  : null;

              const usingAnnual = !!showAnnualPricing;
              const shownUnit = "/month";

              const perMonthBase = usingAnnual ? baseYearly / 12 : baseMonthly;
              const perMonthDiscounted = usingAnnual ? (yearlyDiscounted != null ? yearlyDiscounted / 12 : null) : monthlyDiscounted;

              const hasTrialMonthly = computeTrialDays({ discounts: discountMonthly?.discounts || [] }) > 0;
              const hasTrialYearly = computeTrialDays({ discounts: discountYearly?.discounts || [] }) > 0;

              const showDiscountForCurrentView =
                (perMonthDiscounted != null && perMonthDiscounted < perMonthBase) || (usingAnnual ? hasTrialYearly : hasTrialMonthly);
              const showDiscountAny =
                (monthlyDiscounted != null && monthlyDiscounted < baseMonthly) ||
                (yearlyDiscounted != null && yearlyDiscounted < baseYearly) ||
                hasTrialMonthly ||
                hasTrialYearly;

              const chosenForView = usingAnnual ? discountYearly || discountMonthly : discountMonthly || discountYearly;
              const chosenDiscounts = Array.isArray(chosenForView?.discounts) ? chosenForView.discounts : [];
              const hasTrialForView = computeTrialDays({ discounts: chosenDiscounts }) > 0;
              const hasAnyPriceDiscount = chosenDiscounts.some((d) => d?.percent_off != null || d?.amount_off != null);
              const trialDaysForView = computeTrialDays({ discounts: chosenDiscounts });
              const trialAddonLabel =
                hasAnyPriceDiscount && chosenDiscounts.length === 1
                  ? getSingleDiscountAddonLabel(chosenDiscounts)
                  : hasAnyPriceDiscount
                    ? "discounts applied"
                    : null;
              const bigPrice =
                showDiscountForCurrentView && perMonthDiscounted != null && perMonthDiscounted < perMonthBase ? perMonthDiscounted : perMonthBase;
              const bigPriceShown = hasTrialForView ? 0 : bigPrice;
              const afterTrialPerMonth =
                perMonthDiscounted != null && perMonthDiscounted < perMonthBase ? perMonthDiscounted : perMonthBase;

              const yearlyTotal = yearlyDiscounted != null ? yearlyDiscounted : baseYearly;
              const hasStacked = (chosenDiscounts || []).some((d) => d?.source === "global") && (chosenDiscounts || []).some((d) => d?.source === "coupon");
              const globalLabel = (chosenDiscounts || []).find((d) => d?.source === "global")?.name;
              const couponLabel = (chosenDiscounts || []).find((d) => d?.source === "coupon")?.name;

              return (
                <div
                  key={plan.name}
                  className={`relative rounded-lg border-2 p-4 sm:p-6 flex flex-col h-full ${getPlanCardStyle({ isPopular, isCurrent })}`}
                >
                  {isPopular && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <span
                        className={`${interactive.primaryBg} ${planBadgeTextClass}`}
                      >
                        Most Popular
                      </span>
                    </div>
                  )}
                  {isCurrent && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <span className={`bg-green-400 dark:bg-green-500 ${planBadgeTextClass}`}>
                        Current Plan
                      </span>
                    </div>
                  )}
                  {isAccessGrantPlan && (
                    <div className="absolute -top-10 left-1/2 transform -translate-x-1/2">
                      <span className={`bg-blue-500 ${planBadgeTextClass}`}>
                        Access Coupon
                      </span>
                    </div>
                  )}

                  <div className="text-center flex-grow">
                    <h3 className={`${typography.h5} ${textColors.primary}`}>{plan.display_name}</h3>
                    <div className="mt-4">
                      <div className="flex flex-col items-center gap-1">
                        <div className="flex items-baseline justify-center gap-2 flex-wrap">
                          <span className={`${typography.h2} ${textColors.primary}`}>{formatPrice(bigPriceShown)}</span>
                          <span className={`${typography.body} ${textColors.secondary}`}>{shownUnit}</span>
                        </div>

                        {hasTrialForView && (
                          <div className={`text-center ${typography.caption} ${textColors.tertiary}`}>
                            {`Then ${formatPrice(afterTrialPerMonth)}${shownUnit} after free trial ends`}
                          </div>
                        )}

                        {usingAnnual && (
                          <div className="flex items-center justify-center gap-2 flex-wrap">
                            <span className={`${typography.bodySmall} ${textColors.secondary}`}>
                              {hasTrialForView ? `Then ${formatPrice(yearlyTotal)} /year` : `${formatPrice(yearlyTotal)} /year`}
                            </span>
                            {annualSavings > 0 && (
                              <span className={`${typography.caption} font-medium text-green-600 dark:text-green-400`}>
                                Save +{annualSavings}%
                              </span>
                            )}
                          </div>
                        )}

                        {showDiscountAny && (
                          <div className="mt-2 flex flex-col items-center gap-2">
                            <div className={couponBoxClass}>
                              <div className="flex items-center justify-center gap-2 flex-wrap">
                                {hasStacked ? (
                                  <span className={`${couponPillClass} ${typography.caption} font-medium`}>
                                    <StackIcon className="w-4 h-4" title="Stacked discounts" />
                                    Stacked discounts
                                  </span>
                                ) : null}
                                {globalLabel ? (
                                  <span className={`${couponPillClass} ${typography.caption} font-medium`}>
                                    <SparkleIcon className="w-4 h-4" title="Global discount" />
                                    {globalLabel}
                                  </span>
                                ) : null}
                                {couponLabel ? (
                                  <span className={`${couponPillClass} ${typography.caption} font-medium`}>
                                    <CouponIcon className="w-4 h-4" title="Coupon" />
                                    {couponLabel}
                                  </span>
                                ) : null}
                              </div>
                              {hasTrialForView && (
                                <div className={`mt-1 text-center ${typography.caption} ${couponGreenText} font-medium`}>
                                  {formatTrialLabel(trialDaysForView)}
                                  {trialAddonLabel ? ` + ${trialAddonLabel}` : ""}
                                </div>
                              )}

                              <div className="mt-2 flex flex-col items-center gap-1">
                                {!usingAnnual && monthlyDiscounted != null && monthlyDiscounted < baseMonthly && (
                                  <div className={`flex items-center justify-center gap-3 flex-wrap ${typography.bodySmall} ${couponGreenText}`}>
                                    <span className="line-through">{formatPrice(baseMonthly)}</span>
                                    <span className="font-medium">{formatPrice(monthlyDiscounted)}</span>
                                  </div>
                                )}

                                {usingAnnual && (
                                  <>
                                    {yearlyDiscounted != null && yearlyDiscounted < baseYearly ? (
                                      <>
                                        <div className={`flex items-center justify-center gap-3 flex-wrap ${typography.bodySmall} ${couponGreenText}`}>
                                          <span className="line-through">{formatPrice(baseYearly / 12)}</span>
                                          <span className="font-medium">{formatPrice(yearlyDiscounted / 12)}</span>
                                        </div>
                                        <div className={`flex items-center justify-center gap-3 flex-wrap ${typography.bodySmall} ${couponGreenText}`}>
                                          <span className="line-through">{formatPrice(baseYearly)} /year</span>
                                          <span className="font-medium">{formatPrice(yearlyDiscounted)} /year</span>
                                        </div>
                                      </>
                                    ) : couponLabel && !hasTrialYearly ? (
                                      <div className={`${typography.caption} ${textColors.muted}`}>Coupon doesn’t apply to annual billing.</div>
                                    ) : null}
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    <p className={`${typography.bodySmall} ${textColors.secondary} mt-2`}>{plan.description}</p>

                    <ul className="mt-6 space-y-3">
                      {plan.highlights?.map((highlight, index) => (
                        <li key={index} className={`flex items-center ${typography.bodySmall}`}>
                          {plan?.name === "free" && /^\s*0\b/.test(String(highlight)) && /published websites/i.test(String(highlight)) ? (
                            <XIcon />
                          ) : (
                            <CheckIcon />
                          )}
                          <span className={`ml-2 ${textColors.secondary}`}>{highlight}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="mt-6">
                    {/* Upgrades-only: hide action entirely for downgrades */}
                    {isDowngrade ? null : (
                    <button
                      type="button"
                      disabled={isCurrent && !isAccessGrantPlan}
                      onClick={() => onSelectPlan?.(plan)}
                      className={`w-full py-3 px-4 rounded-lg ${typography.button} font-medium transition-colors ${getButtonStyle({
                        isPopular,
                        isCurrent: isCurrent && !isAccessGrantPlan,
                      })}`}
                    >
                      {isCurrent
                        ? isAccessGrantPlan
                          ? `Keep ${plan.display_name} after access ends`
                          : "Current Plan"
                        : `Upgrade to ${plan.display_name}`}
                    </button>
                    )}
                  </div>
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
};

export default AvailablePlans;



